<script setup lang="ts">
import { checkSavedCard } from '@/utils/checkSavedCard'

import type { DeliveryMethods } from '@/types/api/api-v3/basket'
import type { BasketPayment } from '@/types/api/api-v3/paymentMethods'

import BasketPaymentMethod from '@/components/Basket/basket-payment/BasketPaymentMethod.vue'
import BasketPaymentSavedCard from '@/components/Basket/basket-payment/BasketPaymentSavedCard.vue'

type Props = {
  paymentItem: BasketPayment
  isActive: boolean
  methodDelivery: DeliveryMethods
}

const props = defineProps<Props>()

const emit = defineEmits(['update:selected-payment'])

const onUpdateSelectedPayment = () => {
  emit('update:selected-payment', props.paymentItem)
}

const paymentItemClasses = computed(() => {
  const classes = ['payment-item payment-item--saved-card']

  if (!checkSavedCard(props.paymentItem)) {
    return classes
  }

  if (props.paymentItem.is_expired) {
    classes.push('payment-item--disabled')
  }

  if (props.isActive) {
    classes.push('payment-item--active')
  }

  return classes
})
</script>

<template>
  <div v-if="checkSavedCard(paymentItem)" :class="paymentItemClasses">
    <basket-payment-saved-card
      :saved-card="paymentItem"
      @click="onUpdateSelectedPayment"
    />
  </div>

  <basket-payment-method
    v-else
    class="payment-item"
    :class="{ 'payment-item--active': isActive }"
    :payment="paymentItem"
    :method-delivery="methodDelivery"
    @click="onUpdateSelectedPayment"
  />
</template>

<style scoped lang="scss">
.payment-item {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: space-between;
  width: 100px;
  min-width: 100px;
  min-height: 64px;
  padding: 8px;
  overflow: hidden;
  cursor: pointer;
  background: var(--ui-grey);
  border-radius: 10px;
  outline: 2px solid transparent;
  outline-offset: -2px;
  transition: outline-color 0.15s ease-in-out;

  @include media('>=tablet') {
    gap: 7px;
    width: 132px;
    min-width: 132px;
    min-height: 74px;
    padding: 12px;
  }

  &--saved-card {
    padding: 6px;
    background: var(--ui-white);
    box-shadow: var(--ui-card-shadow);
  }

  &--disabled {
    background: var(--ui-grey);
  }

  &--active {
    outline: 2px solid var(--ui-green);
  }
}
</style>
